import { defineComponent, h } from 'vue'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { renderBsField } from '../field/BsField'
import { useVm } from '../../../composables/useVm'
import { pushUrlHash } from '../../../composables/useUrlFilters'
import { ValueLabel } from '../types'
import { emitModel } from '../emitModel'

/**
 * A row of segmented options — the tab strip at the top of the settings and arc screens.
 *
 * `navigable` makes the selection survive a refresh by writing it to the URL hash, which is why this
 * reads `$route` on mount: the hash is the source of truth for which option is active.
 *
 * Auto-save is handled by hand (`autoSaveMethod: ''` turns the shared listener off) because the value
 * has to be emitted before the save runs, so a `beforeSave` can read the new v-model.
 */
export default defineComponent({
  name: 'BsToggle',

  // See BsInput: undeclared DOM-named events are bound twice, once natively.
  emits: [ 'input', 'update:modelValue' ],

  props: {
    ...bsComponentProps,
    value: {
      type: [String, Number] as unknown as () => string | number,
      default: void 0,
      validator: (prop: unknown) =>
        typeof prop === 'string' ||
        typeof prop === 'number' ||
        prop === null
    },
    options: {
      type: Array as unknown as () => ValueLabel[],
      default: void 0
    },
    evenSpacing: { type: Boolean, default: true },
    toggleWrapperClass: { type: String, default: void 0 },
    toggleClass: { type: String, default: void 0 },
    navigable: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    const vm = useVm()

    return {
      ...useBsComponent({
        vm,
        props,
        emit,
        value: () => props.value,
        // We're handling this manually.
        autoSaveMethod: ''
      })
    }
  },

  methods: {
    updateUrl (step: string | number | undefined) {
      // Update the url so when the page is refreshed it loads the same tab that the user was on —
      // through the router's history, or the next click anywhere puts it back (sc-28993).
      if (this.navigable && step !== void 0) {
        pushUrlHash(this.$router, step)
      }
    },

    /** Renamed from `__renderOption`: Vue does not proxy setup/method keys beginning with `_`. */
    renderOption (option: ValueLabel) {
      return h('div', {
        class: ['bs-toggle__option col', [{
          'bs-toggle__option--active': this.value === option.value,
          'bs-toggle__option--even-spacing': this.evenSpacing,
          'bs-toggle__option--disabled': option.disable
        }]],
        onClick: () => {
            if (!option.disable) {
              emitModel(this, option.value) // Input the value before saving so the v-model is usable in beforeSave
              this.updateUrl(option.value)
              this.doAutoSave()
            }
          },
        ...this.getAttributes()
      }, [
        option.label
      ])
    },

    renderOptions () {
      return this.options.map(option => this.renderOption(option))
    },

    renderToggle () {
      return h('div', {
        class: ['bs-toggle row', this.toggleClass]
      }, this.renderOptions())
    },

    renderContainer () {
      return h('div', {
        class: ['full-width', this.toggleWrapperClass]
      }, [
        this.renderToggle()
      ])
    }
  },

  mounted () {
    if (this.navigable) {
      const hash = this.$route.hash
      if (hash !== void 0 && hash !== '') {
        emitModel(this, parseInt(hash.replace('#', '')))
      }
    }
  },

  render () {
    return renderBsField(this, 'bs-toggle__container', this.renderContainer())
  }
})
